// Central reference for all email/template placeholders supported by the app.
// This is intended as a user-facing reference (Templates tab / docs), not as the source of truth
// for parsing/replacement logic.

export const TEMPLATE_FIELDS = [
  // Mail-merge fields (applyMerge / buildMergeDataForRecipient)
  {
    token: '{{name}}',
    label: 'First name',
    description: 'Recipient first name (nickname if set; otherwise first word of properName/name).',
    scope: 'mailMerge',
    contexts: ['templateTest', 'subEvent', 'flyer', 'team', 'manifest'],
  },
  {
    token: '{{team}}',
    label: 'Team',
    description: 'Team label/number when available.',
    scope: 'mailMerge',
    contexts: ['team', 'manifest', 'templateTest'],
  },
  {
    token: '{{mentor}}',
    label: 'Mentor',
    description: 'Mentor display name when available.',
    scope: 'mailMerge',
    contexts: ['team', 'manifest', 'templateTest'],
  },
  {
    token: '{{eventTitle}}',
    label: 'Event title',
    description: 'Event title/name, when event context is available.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'team', 'subEvent', 'flyer', 'manifest'],
  },
  {
    token: '{{subEventLabel}}',
    label: 'Sub-event label',
    description: 'Sub-event label such as “After Party”.',
    scope: 'mailMerge',
    contexts: ['subEvent', 'templateTest'],
  },

  {
    token: '{{firstName}}',
    label: 'First name (greeting)',
    description: 'Same as {{name}}; first name for greeting (Recurrency reminder).',
    scope: 'mailMerge',
    contexts: ['recurrency_update', 'recurrency_class_reminder', 'templateTest'],
  },
  {
    token: '{{checkInTime}}',
    label: 'Check-in time',
    description: 'Recurrency only: recipient\'s scheduled check-in time from their chosen slot, or —.',
    scope: 'mailMerge',
    contexts: ['recurrency_update', 'recurrency_class_reminder', 'templateTest'],
  },
  {
    token: '{{classTime}}',
    label: 'Class time',
    description: 'Recurrency only: recipient\'s scheduled class start time from their chosen slot, or —.',
    scope: 'mailMerge',
    contexts: ['recurrency_update', 'recurrency_class_reminder', 'templateTest'],
  },
  {
    token: '{{recurrencyUpdateUrl}}',
    label: 'Recurrency update URL',
    description: 'Recurrency only: event-specific link to the recurrency update form.',
    scope: 'mailMerge',
    contexts: ['recurrency_update', 'recurrency_class_reminder', 'templateTest'],
  },
  {
    token: '{{eventWeekRange}}',
    label: 'Event week date range',
    description: 'Wed–Mon range with year including after party (e.g. “March 18–23, 2026”) from event calendar settings.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer', 'team', 'mentor_warning', 'recurrency_update', 'recurrency_class_reminder', 'fury_lab_initial', 'fury_lab_reminder'],
  },
  {
    token: '{{eventDateRange}}',
    label: 'Event week date range (alias)',
    description: 'Same as {{eventWeekRange}}.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer', 'team', 'mentor_warning', 'recurrency_update', 'recurrency_class_reminder', 'fury_lab_initial', 'fury_lab_reminder'],
  },
  {
    token: '{{recurrencyDate}}',
    label: 'Recurrency calendar date',
    description: 'Full date for the recurrency class day (default Wednesday of event week; overridable in Event Management).',
    scope: 'mailMerge',
    contexts: ['recurrency_update', 'recurrency_class_reminder', 'templateTest'],
  },
  {
    token: '{{recurrencyWeekday}}',
    label: 'Recurrency weekday name',
    description: 'Weekday only (e.g. “Wednesday”) for the recurrency day.',
    scope: 'mailMerge',
    contexts: ['recurrency_update', 'recurrency_class_reminder', 'templateTest'],
  },
  {
    token: '{{furyLabDate}}',
    label: 'Fury Lab calendar date',
    description: 'Full date for Fury Lab (default Thursday; overridable per event).',
    scope: 'mailMerge',
    contexts: ['fury_lab_initial', 'fury_lab_reminder', 'templateTest'],
  },
  {
    token: '{{waterTrainingThursdayDate}}',
    label: 'Water training (Thursday) date',
    description: 'Full calendar date; default Thursday of event week.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer'],
  },
  {
    token: '{{waterTrainingFridayDate}}',
    label: 'Water training (Friday) date',
    description: 'Full calendar date; default Friday of event week.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer'],
  },
  {
    token: '{{canopyThursdayDate}}',
    label: 'Canopy course (Thursday) date',
    description: 'Full calendar date; default Thursday of event week.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer'],
  },
  {
    token: '{{canopyFridayDate}}',
    label: 'Canopy course (Friday) date',
    description: 'Full calendar date; default Friday of event week.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer'],
  },
  {
    token: '{{highAltitudeDate}}',
    label: 'High altitude loads date',
    description: 'Full calendar date; default Sunday of event week.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer'],
  },
  {
    token: '{{afterPartyDate}}',
    label: 'After party date',
    description: 'Monday after main Saturday (Sat + 2) unless overridden in schedule day dates.',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer', 'after_party_desc_budget'],
  },
  {
    token: '{{activityDatesHtml}}',
    label: 'Activity dates (HTML list)',
    description: 'Preformatted HTML unordered list of major activity dates (for HTML bodies).',
    scope: 'mailMerge',
    contexts: ['templateTest', 'flyer', 'mentor_warning'],
  },

  // Legacy HTML tokens (replaced in EmailComposerModal template loading)
  {
    token: '{{TEAM_GREETING_LINE}}',
    label: 'Team greeting line',
    description: 'Greeting line like “Hey Amanda, Chris, Zach!” (Team intro only).',
    scope: 'legacyHtmlToken',
    contexts: ['team_intro'],
  },
  {
    token: '{{TEAM_MEMBERS_HTML}}',
    label: 'Team members HTML',
    description: 'Rendered HTML block listing Freshlings + Mentor (Team intro only).',
    scope: 'legacyHtmlToken',
    contexts: ['team_intro'],
  },
  {
    token: '{{AFTER_PARTY_DATE}}',
    label: 'After Party date',
    description: 'Monday after-party date (from schedule Monday / Sat + 2), same as {{afterPartyDate}} in merge.',
    scope: 'legacyHtmlToken',
    contexts: ['after_party_desc_budget'],
  },
  {
    token: '{{TEAM_LINES_HTML}}',
    label: 'Mentor load summary lines',
    description: 'Rendered HTML lines for mentor load summary table.',
    scope: 'legacyHtmlToken',
    contexts: ['mentor_load_summary'],
  },
  {
    token: '{{REG_LINK}}',
    label: 'Registration link',
    description: 'Registration URL inserted into mentor load summary.',
    scope: 'legacyHtmlToken',
    contexts: ['mentor_load_summary'],
  },
  {
    token: '{{DASHBOARD_BASE_URL}}',
    label: 'Dashboard base URL',
    description: 'Base URL for dashboard links (mentor warning / recurrency update).',
    scope: 'legacyHtmlToken',
    contexts: ['mentor_warning', 'recurrency_update', 'recurrency_class_reminder'],
  },
  {
    token: '{{MENTOR_FIRST_NAME}}',
    label: 'Mentor first name',
    description: 'Mentor first name used in the mentor warning HTML template.',
    scope: 'legacyHtmlToken',
    contexts: ['mentor_warning'],
  },
  {
    token: '{{EVENT_DATE}}',
    label: 'Event date',
    description: 'Formatted main competition date (Saturday) used in the mentor warning HTML template.',
    scope: 'legacyHtmlToken',
    contexts: ['mentor_warning'],
  },
  {
    token: '{{EVENT_WEEK_RANGE}}',
    label: 'Event week range',
    description: 'Wed–Mon range with year (includes after party); filled when loading the mentor warning HTML template.',
    scope: 'legacyHtmlToken',
    contexts: ['mentor_warning'],
  },

  // Fury Lab (separate placeholder system today)
  {
    token: '{{participantName}}',
    label: 'Fury Lab participant name',
    description: 'Fury Lab only: participant display name.',
    scope: 'furyLab',
    contexts: ['fury_lab_initial', 'fury_lab_reminder'],
  },
  {
    token: '{{coachName}}',
    label: 'Fury Lab coach name',
    description: 'Fury Lab only: primary coach name for the participant or time slot.',
    scope: 'furyLab',
    contexts: ['fury_lab_initial', 'fury_lab_reminder'],
  },
  {
    token: '{{eventDate}}',
    label: 'Fury Lab session date',
    description: 'Fury Lab only: resolved Fury Lab calendar date (same as {{furyLabDate}} when merge data is built).',
    scope: 'furyLab',
    contexts: ['fury_lab_initial', 'fury_lab_reminder'],
  },
  {
    token: '{{meetTime}}',
    label: 'Fury Lab meet time',
    description: 'Fury Lab only: meet time string.',
    scope: 'furyLab',
    contexts: ['fury_lab_initial', 'fury_lab_reminder'],
  },
  {
    token: '{{blockSummary}}',
    label: 'Fury Lab block summary',
    description: 'Fury Lab only: list of assigned blocks.',
    scope: 'furyLab',
    contexts: ['fury_lab_initial', 'fury_lab_reminder'],
  },
];

export const TEMPLATE_FIELD_SCOPES = [
  { key: 'mailMerge', label: 'Mail-merge ({{name}} etc.)' },
  { key: 'legacyHtmlToken', label: 'HTML tokens ({{TEAM_MEMBERS_HTML}} etc.)' },
  { key: 'furyLab', label: 'Fury Lab placeholders' },
];

